import { weightMixins } from '@/utils/weightType'
import { categoryTypes } from '@/utils/categories'
import { convertPricePerWeight } from '@/utils/paymentTypes'
import { taxMode, discountApplicableType, discountType, discountRule } from '@/utils/enums'
import moment from 'moment'

export default {
  getProducts: state => {
    return state.products_list.reduce((coll, element) => {
      let quantity = element.quantity

      if (element.holdSalesItem) {
        element.holdSalesItem.forEach(item => {
          if (element.weight_type === item.weight_type) {
            quantity = quantity - weightMixins.methods.mathRound(item.hold_qty, 3)
          } else {
            const tempQty = weightMixins.methods.convertAvailableQty(item.hold_qty, item.weight_type)[element.weight_type] // calculate weight
            quantity = quantity - weightMixins.methods.mathRound(tempQty, 3)
          }
        })
      }

      if (weightMixins.methods.mathRound(quantity, 3) > 0) {
        coll.push({ ...element, quantity })
      }
      return coll
    }, [])
  },

  // convert redeem point to dollar
  convertRedeemPointToDollar: (state, getters, rootState) => {
    return point => {
      const redeemPointSetting = rootState.setting.rewardSetup
      if (!redeemPointSetting) {
        return 0
      }

      return Math.max((point * redeemPointSetting.redeem_dollar) / redeemPointSetting.redeem_points || 0, 0)
    }
  },

  // convert redeem dollar to points
  convertRedeemDollarToPoints: (state, getters, rootState) => {
    return dollar => {
      const redeemPointSetting = rootState.setting.rewardSetup
      if (!redeemPointSetting) {
        return 0
      }

      return (dollar * redeemPointSetting.redeem_points) / redeemPointSetting.redeem_dollar
    }
  },

  // calculate item wise redeem points
  calculateItemWiseRedeemPoints: (state, getters, rootState) => {
    const redeemPointSetting = rootState.setting.rewardSetup
    return item => {
      if (state.remainingRedeemPoints && rootState.setting.rewardSetup) {
        const tempRedeemPoints = (getters.calculateBaseSubTotal(item) * redeemPointSetting.redeem_points) / redeemPointSetting.redeem_dollar
        const applicablePoints = state.remainingRedeemPoints < tempRedeemPoints ? state.remainingRedeemPoints : tempRedeemPoints

        return Math.ceil(applicablePoints)
      }

      return 0
    }
  },

  // convert discount amount to amount
  convertDiscountIntoAmount: (state, getters) => {
    return (item, discount, discounType) => {
      return discounType === 1 ? (discount * getters.calculateBaseSubTotal(item)) / 100 : Number(discount)
    }
  },

  // get customer list
  getCustomersList: state => {
    return state.customers.map(x => {
      return {
        ...x,
        label: x.business_name ? x.business_name : `${x.first_name} ${x.last_name}`
      }
    })
  },

  // check product category
  isItemCategoryFlowerOrShake: (state, getters, rootState) => {
    const categories = rootState.product.categories
    return item => [categories.FLOWER, categories.SHAKE].includes(item.categoryId)
  },

  // calculate item price
  calculateItemPrice: (state, getters) => {
    return item => {
      const newPrice = item.tieredPricingGroup ? getters.calculateTieredPrice(item) : convertPricePerWeight(item.orignalweightType, item.sales_price)[item.weight_type]
      // reduce price if price is tax included
      if (state.item__taxes.length && state.item__taxes[0].tax_mode === taxMode.taxIncluded) {
        return Math.max(0, getters.calculateItemPriceBasedOnTaxType(item, newPrice)) || 0
      }

      return Math.max(0, newPrice) || 0
    }
  },

  // calculate item price based on tax
  calculateItemPriceBasedOnTaxType: (state, getters) => {
    return (item, price) => {
      if (state.item__taxes.length && state.item__taxes[0].tax_mode === taxMode.taxIncluded) {
        return (price * 100) / (100 + getters.getItemTaxRate(item.categoryId))
      }
    }
  },

  // calculate tiered price
  calculateTieredPrice: (state, getters) => {
    return item => {
      let tieredPricingObj = null

      const buyQtyGrms = !getters.isItemCategoryFlowerOrShake(item) ? weightMixins.methods.convertAvailableQty(item.buyQty, item.weight_type)['grams'] : item.buyQty

      tieredPricingObj = item.tieredPricingGroup.pricingFlows.find(x => {
        return !!(x.weight_quantity === buyQtyGrms || (buyQtyGrms >= x.weight_quantity && buyQtyGrms <= x.weight_quantity + item.tieredPricingGroup.weightHeavyAmount))
      })

      if (tieredPricingObj) {
        return tieredPricingObj.pricing_value / buyQtyGrms
      } else {
        return convertPricePerWeight(item.orignalweightType, item.sales_price)[item.weight_type]
      }
    }
  },

  /**
   * Calculate PROMO discount
   */
  calculatePromoDiscount: (state, getters, rootState) => {
    return item => {
      const discount = {
        amount: 0,
        type: 1
      }

      // list of promo discount
      const promoDiscountList = rootState.setting.userDiscountAndTax

      if (state.customerDetail && state.customerDetail.taxDiscount) {
        discount.amount += state.customerDetail.taxDiscount.value
      }

      if (state.customerDetail && promoDiscountList.length) {
        promoDiscountList.forEach(x => {
          // if status true
          if (!x.status) {
            return false
          }

          // check week day
          if (x.WeekOfDay && x.WeekOfDay.length) {
            if (!x.WeekOfDay.includes(moment().format('dddd'))) {
              return false
            }
          }

          // check start date and end date
          if (x.startTime && x.endTime) {
            const startTime = x.startTime
            const endTime = x.endTime
            const currentTime = moment().format()

            if (!moment(currentTime).isBetween(startTime, endTime)) {
              return false
            }
          }

          // customer status
          if (
            discountRule.customer_status.replaceAll(' ', '') === x.discount_on &&
            state.customerDetail.customerStatusIds.length &&
            x.customerStatusId &&
            state.customerDetail.customerStatusIds.some(r => x.customerStatusId.includes(r))
          ) {
            if (x.discount_type.toLowerCase() !== discountType.percentage && discount.amount > 0) {
              discount.amount = (discount.amount * getters.calculateBaseSubTotal(item)) / 100
              discount.type = 2
            }
            discount.amount += x.value

            return false
          }

          // customer birthdate
          if (
            discountRule.customer_birthDate.replaceAll(' ', '') === x.discount_on &&
            x.customer_birthdate &&
            moment(x.customer_birthdate).format('YYYY-MM-DD') === moment(state.customerDetail.dob).format('YYYY-MM-DD')
          ) {
            if (x.discount_type.toLowerCase() !== discountType.percentage && discount.amount > 0) {
              discount.amount = (discount.amount * getters.calculateBaseSubTotal(item)) / 100
              discount.type = 2
            }
            discount.amount += x.value

            return false
          }

          // new customer
          if (state.customerDetail.new_customer && discountRule.new_customer.replaceAll(' ', '') === x.discount_on) {
            if (x.discount_type.toLowerCase() !== discountType.percentage && discount.amount > 0) {
              discount.amount = (discount.amount * getters.calculateBaseSubTotal(item)) / 100
              discount.type = 2
            }
            discount.amount += x.value

            return false
          }

          // check items
          if (discountRule.item.replaceAll(' ', '') === x.discount_on && x.itemIds && x.itemIds.length) {
            if (x.itemIds.includes(item.id)) {
              if (x.discount_type.toLowerCase() !== discountType.percentage && discount.amount > 0) {
                discount.amount = (discount.amount * getters.calculateBaseSubTotal(item)) / 100
                discount.type = 2
              }
              discount.amount += x.value
            }

            return false
          }

          // check categories
          if (discountRule.category.replaceAll(' ', '') === x.discount_on && x.categoryIds && x.categoryIds.length) {
            if (x.categoryIds.includes(item.categoryId)) {
              if (x.discount_type.toLowerCase() !== discountType.percentage && discount.amount > 0) {
                discount.amount = (discount.amount * getters.calculateDiscountAmount(item)) / 100
                discount.type = 2
              }
              discount.amount += x.value
            }

            return false
          }
        })
      }

      return discount
    }
  },

  // calculate discount amount
  calculateDiscountAmount: (state, getters) => {
    return item => {
      return item.discountType === 1 ? (item.discount * getters.calculateBaseSubTotal(item)) / 100 : Number(item.discount)
    }
  },

  // calculatye base sub total
  calculateBaseSubTotal: (state, getters) => {
    return item => {
      const baseSubTotal = getters.calculateItemPrice(item) * item.buyQty

      if (state.item__taxes.length && state.item__taxes[0].tax_mode === taxMode.taxIncluded && item.discountAmountType === discountApplicableType.postTax && item.discount > 0) {
        return item.sales_price * item.buyQty
      }

      // reduce price if price is tax not included
      if (state.item__taxes.length && state.item__taxes[0].tax_mode === taxMode.taxNotIncluded && item.discountAmountType === discountApplicableType.postTax && item.discount > 0) {
        return baseSubTotal + (baseSubTotal * getters.getItemTaxRate(item.categoryId)) / 100
      }

      return baseSubTotal
    }
  },

  // calculate item sub total
  calculateItemSubTotal: (state, getters) => {
    return item => {
      if (item.discountAmountType === discountApplicableType.postTax && item.discount > 0) {
        return Math.max(
          0,
          (getters.calculateBaseSubTotal(item) - getters.calculateDiscountAmount(item) - getters.convertRedeemPointToDollar(item.itemRedeemPoints)) /
            (1 + getters.getItemTaxRate(item.categoryId) / 100)
        )
      }

      return Math.max(0, getters.calculateItemPrice(item) * item.buyQty - getters.calculateDiscountAmount(item) - getters.convertRedeemPointToDollar(item.itemRedeemPoints))
    }
  },

  // get item tax rate
  getItemTaxRate: state => {
    const itemTaxTypeArray = state.invoiceTaxInfo.map(x => x.taxSlug)
    const invoiceTaxEnableStatus = state.invoiceTaxInfo.reduce((acc, x) => {
      acc[x.taxSlug] = x.isTaxEnable

      return acc
    }, {})

    return categoryId => {
      return (
        state.item__taxes.length &&
        state.item__taxes.reduce((acc, item) => {
          if (itemTaxTypeArray.includes(item.taxe_type) && invoiceTaxEnableStatus[item.taxe_type]) {
            acc += item[categoryTypes[categoryId]] || 0
          }

          return acc
        }, 0)
      )
    }
  },

  // calculate each type of  text
  calculateTaxByTaxType: (state, getters) => {
    const invoiceTaxEnableStatus = state.invoiceTaxInfo.reduce((acc, x) => {
      acc[x.taxSlug] = x.isTaxEnable

      return acc
    }, {})

    return (taxType, item = null) => {
      if (!invoiceTaxEnableStatus[taxType]) {
        return 0
      }

      const taxObj = state.item__taxes.length && state.item__taxes.find(x => x.taxe_type === taxType)

      if (item) {
        // tax ratio
        const xTaxRate = taxObj[categoryTypes[item.categoryId]] || 0

        // tax per item
        return Math.max(0, (xTaxRate * getters.calculateItemSubTotal(item)) / 100)
      }

      return state.invoice__products.reduce((acc, elem) => {
        // tax ratio
        const xTaxRate = taxObj[categoryTypes[elem.categoryId]] || 0

        // tax per item
        acc = acc + Math.max(0, (xTaxRate * getters.calculateItemSubTotal(elem)) / 100)

        return acc
      }, 0)
    }
  },

  // calculate item tax
  calculateItemTax: (state, getters) => {
    return item => {
      return state.item__taxes.length && state.item__taxes.reduce((acc, x) => acc + getters.calculateTaxByTaxType(x.taxe_type, item), 0)
    }
  },

  // get total quantity
  getTotalInvoiceQty: state => {
    return state.invoice__products.reduce((acc, item) => acc + item.buyQty, 0)
  },

  // get invoice sub total
  getInvoiceSubTotal: (state, getters) => {
    return state.invoice__products.reduce((acc, item) => acc + getters.calculateItemSubTotal(item), 0)
  },

  // get invoice sub total
  getInvoiceBaseSubTotal: (state, getters) => {
    return state.invoice__products.reduce((acc, item) => acc + getters.calculateBaseSubTotal(item), 0)
  },

  getInvoiceTotalDiscount: (state, getters) => {
    return state.invoice__products.reduce((acc, item) => acc + getters.convertDiscountIntoAmount(item, item.discount, item.discountType), 0)
  },

  // get invoice total redeem points
  getInvoiceTotalRedeemPoint: state => {
    return state.invoice__products.reduce((acc, item) => acc + item.itemRedeemPoints, 0)
  },

  // get invoice total redeem dollar
  getInvoiceTotalRedeemDollar: (state, getters) => {
    return getters.convertRedeemPointToDollar(getters.getInvoiceTotalRedeemPoint)
  },

  // get invoice total tax
  getInvoiceTotalTax: (state, getters) => {
    return state.invoice__products.reduce((acc, item) => {
      const currentItemTax = state.item__taxes.length && state.item__taxes.reduce((acc2, x) => acc2 + getters.calculateTaxByTaxType(x.taxe_type, item), 0)
      acc = acc + currentItemTax
      return acc
    }, 0)
  },

  // get invoice grand total
  getInvoiceGrandTotal: (state, getters) => {
    return getters.getInvoiceSubTotal + getters.getInvoiceTotalTax
  }
}
